import {
  HttpDownloadProgressEvent,
  HttpErrorResponse,
  HttpEventType,
  HttpResponse,
} from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MoveToStatusComponent } from '@components/move-to-status/move-to-status.component';
import { ISignalRNotification, NotificationType } from '@interfaces/signalr-notification';
import { AppDialogService } from '@services/app-dialog.service';
import { LogService } from '@services/log.service';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY, from, merge, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, concatMap, finalize, switchMap, take } from 'rxjs/operators';
import { DSRESTClient } from '../../../shared/communication-objects/restclient.service';
import { api } from '../../../shared/communication-objects/api-path';
import { MoveUsersConfirmationDialogComponent } from '../components/move-users-confirmation-dialog/move-users-confirmation-dialog.component';
import { MoveUsersDialogComponent } from '../components/move-users-dialog/move-users-dialog.component';
import { keysToCamelCase } from '../../../utilities/utils';

export interface MoveableUser {
  userId: number; // System User ID
  userName: string;
  firstName: string;
  lastName: string;
  emailAddress: string;
  organizationName: string;
  direction?: string; // Direction of the move (added during the move process)
}

export interface IMoveDirection {
  direction: string;
  userId: number;
}

export interface IUser {
  userId: number;
  userName: string;
  firstName: string;
  lastName: string;
  organizationName: string;
  billableOrgName: string;
  modifiedByUserName?: string;
  direction?: string;
  destinationOrgName?: string;
  billableOrg?: number;
}

export interface IMoveStatusItem {
  userId: number;
  userName: string;
  firstName: string;
  lastName: string;
  originalBillableOrg: string;
  originalOrg: string;
  destinationBillableOrg: string;
  destinationOrg: string;
  modifiedOn: Date | string;
  modifiedByUserName: string;
  status: string;
  direction: string;
}

export interface IDirectionResponse {
  users: IMoveDirection[];
  destinationOrgName: string;
  billableOrgName: string;
  billableOrg: number;
}

/**
 * Reads the complete Server-Sent Events in `text` from `offset` on. A progress event repeats everything
 * received so far and can end part-way through an event, so the caller keeps the returned offset and
 * passes the longer text back in.
 */
export function readServerSentEvents(
  text: string,
  offset: number
): { notifications: ISignalRNotification[]; offset: number } {
  const notifications: ISignalRNotification[] = [];
  let end = text.indexOf('\n\n', offset);

  while (end !== -1) {
    const block = text.substring(offset, end);
    if (block.startsWith('data: ')) {
      notifications.push(keysToCamelCase(JSON.parse(block.substring('data: '.length))));
    }
    offset = end + 2;
    end = text.indexOf('\n\n', offset);
  }

  return { notifications, offset };
}

export interface IMoveUserRequest {
  destinationOrg: number;
  userIds: number[];
}

export interface IMoveUserResult {
  userId: number;
  status: string;
  direction?: string;
}

export interface IMoveUserResponse {
  success: boolean;
  results: IMoveUserResult[];
}

@Injectable()
export class MoveUserService implements OnDestroy {
  // Static properties and methods
  public static readonly MoveDirections = {
    Down: 'down',
    Up: 'up',
    Sideways: 'sideways',
    Same: 'same',
  };

  public static getIconForDirection(direction: string): string {
    return direction === MoveUserService.MoveDirections.Down
      ? 'arrow_downward'
      : direction === MoveUserService.MoveDirections.Up
      ? 'arrow_upward'
      : direction === MoveUserService.MoveDirections.Sideways
      ? 'swap_horiz'
      : 'info';
  }

  public static getColorForDirection(direction: string): string {
    return direction === MoveUserService.MoveDirections.Down
      ? 'red'
      : direction === MoveUserService.MoveDirections.Up
      ? 'green'
      : direction === MoveUserService.MoveDirections.Sideways
      ? 'red'
      : 'default_color';
  }

  public static isValidDirection(direction: string): boolean {
    return [
      MoveUserService.MoveDirections.Down,
      MoveUserService.MoveDirections.Up,
      MoveUserService.MoveDirections.Sideways,
    ].includes(direction);
  }

  // Instance properties
  private moveMessageSubject = new Subject<ISignalRNotification>();
  public moveMessages$ = this.moveMessageSubject.asObservable();

  private dialogClosedSubject = new Subject<boolean>();
  public dialogClosed$ = this.dialogClosedSubject.asObservable();

  // Dialog references
  private moveStatusDialogRef: MatDialogRef<MoveToStatusComponent>;

  constructor(
    private _logger: LogService,
    private dialog: MatDialog,
    private _ads: AppDialogService,
    private restClient: DSRESTClient,
    private translate: TranslateService
  ) {}

  // Public methods for user movement
  moveUsers(
    selectedUsers: MoveableUser[] | IUser[],
    csoId: number,
    csoBillableOrgId: number,
    csoBillableOrgName: string
  ): Observable<boolean> {
    const moveUserDialog = this.showMoveUsersDialog(
      selectedUsers as IUser[],
      csoId,
      csoBillableOrgId,
      csoBillableOrgName
    );
    return this.handleMoveUsersDialogResult(moveUserDialog).pipe(
      switchMap(dialogResult => {
        if (!dialogResult) {
          return of(false);
        }
        return this.processMoveWithConfirmation(
          selectedUsers as IUser[],
          dialogResult.destinationOrg,
          csoBillableOrgId,
          dialogResult.selectedBillableOrgId ?? csoBillableOrgId
        );
      })
    );
  }

  /**
   * Handles the result from the move users dialog
   */
  private handleMoveUsersDialogResult(
    dialogRef: MatDialogRef<MoveUsersDialogComponent>
  ): Observable<any> {
    return merge(
      dialogRef.componentInstance.onContinue.asObservable(),
      dialogRef.componentInstance.onCancel.asObservable()
    ).pipe(
      finalize(() => dialogRef.close()),
      take(1)
    );
  }

  /**
   * Processes the move after getting destination org and confirmation
   */
  private processMoveWithConfirmation(
    users: IUser[],
    destinationOrg: number,
    csoBillableOrgId: number,
    selectedBillableOrgId: number
  ): Observable<boolean> {
    const showBillableOrgWarning =
      selectedBillableOrgId != null && selectedBillableOrgId !== csoBillableOrgId;
    return this.determineMoveDirection(users, destinationOrg).pipe(
      catchError((error: unknown) => {
        // The drop-down can offer an organization the server will not accept, so this is where a
        // refusal normally lands: before the user has typed MOVE, with nothing changed.
        if (error instanceof HttpErrorResponse && error.status === 403) {
          this._ads.showErrorMessage(this.translate.instant('MoveForms.UserMove.MoveRefused'));
          return of(null);
        }
        return throwError(() => error);
      }),
      switchMap((destinations: IDirectionResponse | null) => {
        if (!destinations) {
          return of(false);
        }
        const preparedUsers = this.prepareUsersForConfirmation(users, destinations);
        return this.showAndHandleConfirmationDialog(
          preparedUsers,
          destinationOrg,
          showBillableOrgWarning
        );
      })
    );
  }

  /**
   * Prepares user data with direction and organization information
   */
  private prepareUsersForConfirmation(users: IUser[], destinations: IDirectionResponse): IUser[] {
    // Assign direction and organization info to each user
    users.forEach(user => {
      user['direction'] = destinations.users.find(dir => dir.userId === user.userId)?.direction;
      user['destinationOrgName'] = destinations.destinationOrgName;
      user['billableOrgName'] = destinations.billableOrgName;
      user['billableOrg'] = destinations.billableOrg;
    });

    // Sort users by direction, lastName, firstName, userName
    return users.sort(
      (a, b) =>
        a.direction.localeCompare(b.direction) ||
        a.lastName.localeCompare(b.lastName) ||
        a.firstName.localeCompare(b.firstName) ||
        a.userName.localeCompare(b.userName)
    );
  }

  /**
   * Shows confirmation dialog and handles the result
   */
  private showAndHandleConfirmationDialog(
    users: IUser[],
    destinationOrg: number,
    showBillableOrgWarning: boolean
  ): Observable<boolean> {
    return this.showUserMoveConfirmationDialog(users, showBillableOrgWarning)
      .afterClosed()
      .pipe(
        take(1),
        switchMap((confirmed: boolean) => {
          if (!confirmed) {
            return of(false);
          }
          return this.initiateMoveProcess(users, destinationOrg);
        })
      );
  }

  /**
   * Initiates the actual move process after confirmation
   */
  private initiateMoveProcess(users: IUser[], destinationOrg: number): Observable<boolean> {
    const initialStatusData = this.prepareInitialStatusData(users);
    this._logger.log('Initial status data:', initialStatusData);

    // Show the status dialog immediately with initial "Waiting" status
    this.showMoveStatusDialog(initialStatusData);

    // Add a longer delay to ensure the dialog is fully rendered and initialized
    return this.waitForDialogInitialization().pipe(
      switchMap(
        () =>
          new Observable<boolean>(observer => {
            let summary: IMoveUserResponse | null = null;

            const subscription = this.moveUsersStream({
              destinationOrg,
              userIds: users.map(u => u.userId),
            }).subscribe({
              next: notification => {
                if (notification.data?.results) {
                  summary = notification.data;
                }
                // Forward each notification to the moveMessageSubject for UI updates
                this.moveMessageSubject.next(notification);
              },
              error: err => {
                this.finishMove(null);
                this.handleMoveError(err);
                observer.error(err);
              },
              complete: () => {
                this.finishMove(summary);
                observer.next(true);
                observer.complete();
              },
            });

            return () => subscription.unsubscribe();
          })
      )
    );
  }

  /**
   * Says how the move ended. No summary means the stream stopped part-way, and nothing says the rows
   * still open were moved.
   */
  private finishMove(summary: IMoveUserResponse | null): void {
    const allMoved = !!summary && summary.results.every(result => result.status === 'Success');
    const statusScreen = this.moveStatusDialogRef?.componentInstance;

    statusScreen?.failOpenItems();
    statusScreen?.setSubtitle(
      allMoved ? 'MoveForms.UserMove.MoveStatusSubtitle' : 'MoveForms.UserMove.MoveStatusWithIssues'
    );
    this.handleMoveCompletion({ success: allMoved });
  }

  /**
   * Creates initial status data for all users
   */
  private prepareInitialStatusData(users: IUser[]): IMoveStatusItem[] {
    return users.map(user => {
      return {
        userId: user.userId,
        userName: user.userName,
        firstName: user.firstName,
        lastName: user.lastName,
        originalBillableOrg: user.billableOrgName,
        originalOrg: user.organizationName,
        destinationBillableOrg: user.billableOrgName,
        destinationOrg: user.destinationOrgName || '',
        modifiedOn: new Date(),
        modifiedByUserName: user.modifiedByUserName || 'System',
        status: 'Waiting',
        direction: user.direction || 'Unknown',
      };
    });
  }

  /**
   * Returns an observable that resolves when dialog is ready
   */
  private waitForDialogInitialization(): Observable<void> {
    return new Observable<void>(observer => {
      this._logger.log('Waiting for dialog to initialize before starting move process...');
      // Small delay to ensure dialog is initialized
      this.moveStatusDialogRef
        .afterOpened()
        .pipe(take(1))
        .subscribe(() => {
          this._logger.log('Dialog fully initialized, starting move process now');
          observer.next();
          observer.complete();
        });
    });
  }

  /**
   * Handles the completion of the move process
   */
  private handleMoveCompletion(_result: { success: boolean }): void {
    if (_result.success) {
      this._ads.showSuccessMessage('Move process completed successfully.');
    } else {
      this._ads.showErrorMessage('Not all users were moved.');
    }
  }

  /**
   * Handles errors during the move process
   */
  private handleMoveError(error: any): void {
    this._logger.error('Error starting move process:', error);

    // Add a manual notification to indicate the error
    const errorNotification: ISignalRNotification = {
      message: 'Error starting move process',
      source: 'MoveUser',
      type: NotificationType.Error,
      timestamp: new Date().toISOString(),
      data: { error: error.message || 'Unknown error' },
    };
    this.moveMessageSubject.next(errorNotification);
  }

  determineMoveDirection(
    _selectedUsers: { userId: number }[],
    destinationOrg: number
  ): Observable<IDirectionResponse> {
    const apiResponse = this.restClient.postForResponse<IDirectionResponse>(
      api`user/move-users/direction/${destinationOrg}`,
      {
        userIds: _selectedUsers.map(user => user.userId),
      }
    );

    return apiResponse.pipe(
      finalize(() => {
        this._logger.log('Direction determination complete for users:', _selectedUsers);
      })
    );
  }

  // Dialog related methods
  private showMoveUsersDialog(
    selectedUsers: IUser[],
    csoId: number,
    csoBillableOrgId: number,
    csoBillableOrgName: string
  ): MatDialogRef<MoveUsersDialogComponent> {
    const dialogRef = this.dialog.open(MoveUsersDialogComponent, {
      width: '80%',
      disableClose: true, // Prevent closing by clicking outside or pressing ESC
      data: {
        selectedUsers,
        csoId,
        csoBillableOrgId,
        csoBillableOrgName,
      },
    });
    return dialogRef;
  }
  private showUserMoveConfirmationDialog(
    selectedUsers: IUser[],
    showBillableOrgWarning: boolean
  ): MatDialogRef<MoveUsersConfirmationDialogComponent> {
    return this._ads.showDialogReturnRef(MoveUsersConfirmationDialogComponent, {
      selectedUsers,
      showBillableOrgWarning,
    });
  }

  private showMoveStatusDialog(selectedUsers: IMoveStatusItem[]) {
    this._logger.log('showMoveStatusDialog called with data:', selectedUsers);

    // If there's already a dialog open, close it first
    if (this.moveStatusDialogRef) {
      this._logger.log('Closing existing move status dialog');
      this.moveStatusDialogRef.close();
      // Small delay to ensure previous dialog is fully closed
      this.moveStatusDialogRef = null;
    }
    this.openMoveStatusDialog(selectedUsers);
  }

  private openMoveStatusDialog(selectedUsers: IMoveStatusItem[]) {
    // Ensure all users have a status of 'Waiting' if not already set
    const usersWithStatus = selectedUsers.map(user => {
      // Check if the status is final (completed or failed)
      const finalStatuses = [
        'success',
        'complete',
        'done',
        'error',
        'failed',
        'rejected',
        'skipped',
      ];
      const isFinalStatus =
        user.status && finalStatuses.some(status => user.status.toLowerCase().includes(status));

      //fix ModifiedOn format
      user['modifiedOn'] = new Date(user.modifiedOn).toLocaleString();
      // If the status is not final, set it to 'Waiting'
      if (!isFinalStatus) {
        // Log if we're changing the status
        if (user.status && user.status !== 'Waiting') {
          this._logger.log(`Changing user ${user.userId} status from ${user.status} to Waiting`);
        }
        return { ...user, status: 'Waiting' };
      }
      return user;
    });

    const columns = [
      'userName',
      'firstName',
      'lastName',
      'userId',
      'originalBillableOrg',
      'originalOrg',
      'destinationBillableOrg',
      'destinationOrg',
      'modifiedOn',
      'modifiedByUserName',
      'status',
      'direction',
    ];

    this._logger.log('Opening move status dialog');

    // Store dialog reference as a class property so we can access it later
    this.moveStatusDialogRef = this.dialog.open(MoveToStatusComponent, {
      panelClass: 'cso-dialog',
      width: '85%',
      autoFocus: false,
      restoreFocus: false,
      disableClose: true, // Prevent closing by clicking outside or pressing ESC
      data: {
        results: usersWithStatus,
        showToListButton: false,
        displayedColumns: columns,
        exportToExcelFileName: 'User_Move_Status',
        title: 'MoveForms.UserMove.MoveStatus',
        subtitle: 'MoveForms.UserMove.MoveStatusInProgress',
        moveMessages$: this.moveMessages$,
      },
    });

    const moveToStatusComponent: MoveToStatusComponent = this.moveStatusDialogRef.componentInstance;

    moveToStatusComponent.close.pipe(take(1)).subscribe(() => {
      this._logger.log('Close event received, isProcessing:', moveToStatusComponent.isProcessing);

      // Only allow closing if all items are complete or processing is done
      if (!moveToStatusComponent.isProcessing || moveToStatusComponent.areAllItemsComplete()) {
        this._logger.log('Closing all dialogs');
        this.dialog.closeAll();

        // Emit that the dialog was closed after a completed move
        this.dialogClosedSubject.next(moveToStatusComponent.areAllItemsComplete());
      }
    });

    /* COLUMNS defined in the JIRA ticket SBTMTAPWP-3041
        User Name
        First Name
        Last Name
        System User ID
        Original Billable Org
            Billable Organization from which the User was moved
        Original Org
            Organization from which the User was moved
        Destination Billable Org
            Billable Organization to which the User was moved to
        Destination Org
            Organization to which the User was moved to
        Date Stamp
        User Name
        Status
    */
  }

  // Lifecycle methods
  ngOnDestroy() {
    if (this.moveStatusDialogRef) {
      this.moveStatusDialogRef.close();
    }
  }

  /**
   * Runs the move. One POST: the server moves each user as this response is read and writes an event
   * for each step, ending in a summary. Parsing failures surface as a stream error, which ends the
   * move as failed rather than leaving rows waiting.
   */
  moveUsersStream(request: IMoveUserRequest): Observable<ISignalRNotification> {
    let offset = 0;

    return this.restClient.postForTextStream(api`user/move-users/stream`, request).pipe(
      concatMap(event => {
        const text =
          event.type === HttpEventType.DownloadProgress
            ? (event as HttpDownloadProgressEvent).partialText
            : event.type === HttpEventType.Response
            ? (event as HttpResponse<string>).body
            : null;
        if (!text) {
          return EMPTY;
        }

        const read = readServerSentEvents(text, offset);
        offset = read.offset;
        return from(read.notifications);
      })
    );
  }
}
